import { Component, Inject, OnInit } from '@angular/core';
import { formatDate } from '@angular/common';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { consts } from '../../../consts';
import { Utils } from '../../../shared/utils';
import { ConstitucionFinanciacionPrivadaModel, FinanciacionPrivadaModel } from './financiacion-privada.model';
import { FinanciacionPrivadaService } from './financiacion-privada.service';
import { ActivityService } from '../../user/activity/activity.service';
import { MasterService } from '../../../modules_v2/master/services/master.service';
import { NotificationService } from '../../../modules_v2/shared/services/notification.service';
import { ConfirmService } from '../../../modules_v2/shared/services/confirm.service';
import { ConfirmDialogDetail } from '../../../modules_v2/shared/components/confirm-dialog.component';
import { stripFinancingConcept } from '../../../modules_v2/finance/financing-detail/financing-detail.model';
import {
  bankAccountOptions,
  currentActivityOption,
  EntityFormOption,
  fromApiDate,
  InjectedOptions,
  optionId,
} from '../../../modules_v2/finance/entity-form/entity-form.utils';

@Component({
    selector: 'convfact-financiacionprivadaconstitucion',
    templateUrl: './financiacion-privada-dialog-constitucion.component.html',
    styleUrls: ['./financiacion-dialogs.component.css'],
    standalone: false
})
export class FinanciacionPrivadaDialogConstitucionComponent implements OnInit {

  public financiacionPrivada: FinanciacionPrivadaModel;
  public editing: ConstitucionFinanciacionPrivadaModel | null = null;

  public dataSource: ConstitucionFinanciacionPrivadaModel[] = [];
  public bankAccounts: EntityFormOption[] = [];
  public activities: EntityFormOption[] = [];

  public ready = false;
  public loadError = false;
  public saving = false;
  public savingMessage = '';
  public submitted = false;
  public saveError = '';

  public form: UntypedFormGroup;

  private model: ConstitucionFinanciacionPrivadaModel = new ConstitucionFinanciacionPrivadaModel();
  private changed = false;
  private requestedEditDone = false;
  private readonly injected = new InjectedOptions();

  constructor(
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<FinanciacionPrivadaDialogConstitucionComponent, boolean>,
    public financiacionPrivadaService: FinanciacionPrivadaService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public activityService: ActivityService,
    private masterService: MasterService,
    private notify: NotificationService,
    private confirm: ConfirmService,
  ) {
    this.financiacionPrivada = data.financiacionPrivada;
    this.form = this.fb.group({
      fecha: [null],
      importe: [null],
      concepto: [''],
      comisionApertura: [0],
      gastosBancarios: [0],
      activity: [null],
      bankAccount: [null],
    });
  }

  get subtitle(): string {
    return [this.financiacionPrivada.descripcion, this.termLabel].filter(Boolean).join(' · ');
  }

  get termLabel(): string {
    if (this.financiacionPrivada.duracion === 1) return 'Corto plazo';
    if (this.financiacionPrivada.duracion === 2) return 'Largo plazo';
    return '';
  }

  get totalConstituted(): number {
    return Utils.RoundTwoDecimals(this.dataSource.reduce((acc, item) => acc + (Number(item.importe) || 0), 0));
  }

  get conceptoLength(): number {
    return String(this.form.get('concepto').value || '').length;
  }

  get missingDate(): boolean { return !this.form.get('fecha').value; }
  get missingConcept(): boolean { return !String(this.form.get('concepto').value || '').trim(); }
  get invalidImporte(): boolean { return !Number(this.form.get('importe').value); }
  get missingActivity(): boolean { return optionId(this.form.get('activity').value) === null; }
  get missingBankAccount(): boolean { return optionId(this.form.get('bankAccount').value) === null; }

  public control(name: string): UntypedFormControl {
    return this.form.get(name) as UntypedFormControl;
  }

  public ngOnInit(): void {
    this.load();
  }

  public retry(): void {
    this.loadError = false;
    this.load();
  }

  public startEdit(item: ConstitucionFinanciacionPrivadaModel): void {
    if (this.saving) return;
    this.editing = item;
    this.submitted = false;
    this.saveError = '';
    this.injected.clear();

    this.model = { ...item } as ConstitucionFinanciacionPrivadaModel;

    this.form.patchValue({
      fecha: fromApiDate(item.fecha),
      importe: item.importe != null ? Number(item.importe) : null,
      concepto: this.itemConcept(item),
      comisionApertura: item.comisionApertura != null ? Number(item.comisionApertura) : null,
      gastosBancarios: item.gastosBancarios != null ? Number(item.gastosBancarios) : null,
      activity: this.injected.ensure(this.activities, item.activitiesUid, (item as any).activitiesName),
      bankAccount: this.injected.ensure(this.bankAccounts, item.bankAccountsUid, item.bankAccountInfo),
    }, { emitEvent: false });
  }

  public cancelEdit(): void {
    this.editing = null;
    this.resetForm();
  }

  public pay(): void {
    if (this.saving) return;
    this.submitted = true;
    this.saveError = '';
    if (this.missingDate || this.missingConcept || this.invalidImporte || this.missingActivity || this.missingBankAccount) {
      return;
    }

    const isEdit = !!this.editing;
    const payload = this.buildPayload();

    const task = isEdit
      ? this.financiacionPrivadaService.constitucionUpdate(payload)
      : this.financiacionPrivadaService.constitucionCreate(payload);

    this.setSaving(true, isEdit ? 'Actualizando constitución…' : 'Registrando constitución…');
    task
      .then(() => {
        this.changed = true;
        this.setSaving(false);
        this.notify.ok(isEdit ? 'Constitución actualizada correctamente' : 'Constitución añadida correctamente');
        if (!isEdit || this.data.editUid) {
          this.dialogRef.close(true);
          return;
        }
        this.editing = null;
        this.resetForm();
        this.reloadList();
      })
      .catch(error => {
        this.setSaving(false);
        this.saveError = this.serverMessage(error) || (isEdit
          ? 'Ha ocurrido un error inesperado al actualizar el elemento'
          : 'Ha ocurrido un error inesperado al añadir el elemento');
      });
  }

  public delete(item: ConstitucionFinanciacionPrivadaModel): void {
    if (this.saving) return;
    this.confirm.open({
      title: 'Eliminar constitución',
      subtitle: this.financiacionPrivada.descripcion || undefined,
      message: 'Se eliminará la constitución y su asiento contable. Si estaba conciliada, el movimiento bancario volverá a quedar pendiente.',
      tone: 'danger',
      details: this.itemDetails(item),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando constitución…',
      errorText: 'Ha ocurrido un error al eliminar la constitución',
      action: () => this.financiacionPrivadaService.constitucionDelete(item.uid),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.changed = true;
      this.notify.ok('Constitución eliminada correctamente');
      if (this.editing && this.editing.uid === item.uid) this.cancelEdit();
      this.reloadList();
    });
  }

  public itemConcept(item: { concepto: string }): string {
    return stripFinancingConcept(item.concepto, this.financiacionPrivada.descripcion);
  }

  public closeDialog(): void {
    if (this.saving) return;
    this.dialogRef.close(this.changed);
  }

  public trackByUid(_: number, item: ConstitucionFinanciacionPrivadaModel): string {
    return item.uid;
  }

  private openRequestedEdit(): void {
    if (!this.data.editUid || this.requestedEditDone) return;
    this.requestedEditDone = true;
    const item = this.dataSource.find(entry => entry.uid === this.data.editUid);
    if (item) this.startEdit(item);
  }

  private load(): void {
    this.ready = false;
    Promise.all([
      this.loadActivities(),
      this.loadBankAccounts(),
      this.financiacionPrivadaService.constitucionList(this.financiacionPrivada.uid),
    ])
      .then(([activities, bankAccounts, list]) => {
        this.activities = activities;
        this.bankAccounts = bankAccounts;
        this.dataSource = list || [];
        this.resetForm();
        this.openRequestedEdit();
      })
      .catch(() => { this.loadError = true; })
      .then(() => { this.ready = true; });
  }

  private reloadList(): void {
    this.financiacionPrivadaService.constitucionList(this.financiacionPrivada.uid)
      .then(list => { this.dataSource = list || []; })
      .catch(() => this.notify.error('No se pudo recargar el listado de constituciones'));
  }

  private loadActivities(): Promise<EntityFormOption[]> {
    return this.activityService.getActiveList()
      .then(list => (list || [])
        .filter(activity => activity && activity.uid && activity.uid !== consts.EMPTY_GUID)
        .map(activity => ({ id: activity.uid, display: activity.name || '' })))
      .catch(() => [] as EntityFormOption[]);
  }

  private loadBankAccounts(): Promise<EntityFormOption[]> {
    return firstValueFrom(this.masterService.getBankAccounts())
      .then(list => bankAccountOptions(list))
      .catch(() => [] as EntityFormOption[]);
  }

  private resetForm(): void {
    this.injected.clear();
    this.submitted = false;
    this.saveError = '';
    this.model = new ConstitucionFinanciacionPrivadaModel();
    this.model.plazo = this.financiacionPrivada.duracion;
    this.form.reset({
      fecha: null,
      importe: null,
      concepto: '',
      comisionApertura: 0,
      gastosBancarios: 0,
      activity: null,
      bankAccount: null,
    }, { emitEvent: false });
    this.applySingleOptionDefaults();
  }

  private applySingleOptionDefaults(): void {
    if (this.editing) return;

    if (this.missingActivity) {
      const current = currentActivityOption(this.activities);
      if (current) this.control('activity').setValue(current, { emitEvent: false });
      else if (this.activities.length === 1) this.control('activity').setValue(this.activities[0], { emitEvent: false });
    }
    if (this.missingBankAccount && this.bankAccounts.length === 1) {
      this.control('bankAccount').setValue(this.bankAccounts[0], { emitEvent: false });
    }
  }

  private buildPayload(): ConstitucionFinanciacionPrivadaModel {
    const value = this.form.getRawValue();
    const payload = { ...this.model } as ConstitucionFinanciacionPrivadaModel;
    payload.financiacionPrivadaId = this.financiacionPrivada.uid;
    payload.plazo = this.financiacionPrivada.duracion;
    payload.fecha = value.fecha;
    payload.importe = Number(value.importe) || 0;
    payload.concepto = String(value.concepto || '').trim();
    payload.comisionApertura = this.optionalAmount(value.comisionApertura);
    payload.gastosBancarios = this.optionalAmount(value.gastosBancarios);
    payload.activitiesUid = optionId(value.activity);
    payload.bankAccountsUid = optionId(value.bankAccount);
    return payload;
  }

  private setSaving(saving: boolean, message = ''): void {
    this.saving = saving;
    this.savingMessage = message;
    this.dialogRef.disableClose = saving;
  }

  private itemDetails(item: ConstitucionFinanciacionPrivadaModel): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [
      { label: 'Importe', value: Utils.FormatMoney(Number(item.importe) || 0) + ' €' },
    ];
    if (item.fecha) details.push({ label: 'Fecha', value: formatDate(item.fecha, 'dd/MM/yyyy', 'es-ES') });
    if (item.concepto) details.push({ label: 'Concepto', value: this.itemConcept(item) });
    if (item.bankAccountInfo) details.push({ label: 'Cuenta bancaria', value: item.bankAccountInfo });
    return details;
  }

  private optionalAmount(value: any): number {
    return value === null || value === undefined || value === '' ? 0 : Number(value) || 0;
  }

  private serverMessage(error: any): string {
    return (error && error.error && error.error.message) || '';
  }
}
